// frontend/src/utils/seasonality.js
//
// Seasonal tendency statistics from DAILY candles: average return and win rate
// grouped by calendar bucket (day of week, day of month, week of year, month,
// quarter). Calendar effects are daily-return phenomena, so callers should feed
// 1D candles regardless of the chart's displayed timeframe.
//
// Every bucket carries its sample count — with ~8 years of dailies, day-of-week
// buckets hold ~400 samples (meaningful) while week-of-year buckets hold ~8
// (weak evidence); consumers must be able to show that difference.

const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const candleDate = (candle) => {
  const ts = candle.timestamp || candle.time;
  if (typeof ts === 'number') {
    return new Date(ts > 1_000_000_000_000 ? ts : ts * 1000);
  }
  return new Date(ts);
};

// ISO-8601 week number (1..53)
const isoWeek = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

const buildBuckets = (labels) => labels.map((label) => ({ label, sum: 0, wins: 0, count: 0 }));

const finalize = (buckets, currentIndex) => buckets.map((b, i) => ({
  label: b.label,
  avg: b.count > 0 ? (b.sum / b.count) * 100 : 0,
  winRate: b.count > 0 ? b.wins / b.count : 0,
  count: b.count,
  isCurrent: i === currentIndex,
}));

/**
 * @param {Array} dailyCandles - candles with close + timestamp/time, daily bars
 * @param {Date} now - "current" date used to mark the active bucket
 * @returns {{dayOfWeek, month, quarter, dayOfMonth, weekOfYear}|null}
 *   each value: {title, data: [{label, avg, winRate, count, isCurrent}]}
 */
export function computeSeasonalStats(dailyCandles, now = new Date()) {
  if (!Array.isArray(dailyCandles) || dailyCandles.length < 200) return null;

  const dow = buildBuckets(DOW_LABELS);
  const month = buildBuckets(MONTH_LABELS);
  const quarter = buildBuckets(['Q1', 'Q2', 'Q3', 'Q4']);
  const dom = buildBuckets(Array.from({ length: 31 }, (_, i) => String(i + 1)));
  const woy = buildBuckets(Array.from({ length: 53 }, (_, i) => String(i + 1)));

  for (let i = 1; i < dailyCandles.length; i++) {
    const prev = Number(dailyCandles[i - 1].close);
    const curr = Number(dailyCandles[i].close);
    if (!Number.isFinite(prev) || !Number.isFinite(curr) || prev <= 0) continue;

    const ret = curr / prev - 1;
    const date = candleDate(dailyCandles[i]);
    if (Number.isNaN(date.getTime())) continue;

    const add = (bucket) => {
      if (!bucket) return;
      bucket.sum += ret;
      bucket.count += 1;
      if (ret > 0) bucket.wins += 1;
    };

    const dayIdx = (date.getDay() + 6) % 7; // Mon=0 .. Sun=6 (weekends matter for crypto)
    add(dow[dayIdx]);
    add(month[date.getMonth()]);
    add(quarter[Math.floor(date.getMonth() / 3)]);
    add(dom[date.getDate() - 1]);
    add(woy[isoWeek(date) - 1]);
  }

  const nowDow = (now.getDay() + 6) % 7;
  // Markets that never trade weekends leave Sat/Sun empty — hide those buckets
  // instead of drawing zero-height bars (crypto keeps all seven).
  const dowData = finalize(dow, nowDow).filter((b) => b.count > 0);
  return {
    dayOfWeek: { title: 'Day of Week', data: dowData },
    month: { title: 'Month', data: finalize(month, now.getMonth()) },
    quarter: { title: 'Quarter', data: finalize(quarter, Math.floor(now.getMonth() / 3)) },
    dayOfMonth: { title: 'Day of Month', data: finalize(dom, now.getDate() - 1) },
    weekOfYear: { title: 'Week of Year', data: finalize(woy, isoWeek(now) - 1) },
  };
}
